<template>
  <div :class="['editable-input-wrapper', {active: focused}]" :data-edit-value="editValue">
    <!-- type = text: Plain text input -->
    <input
      class="editable-input editable-input-active"
      :type="type"
      v-if="isEditing && type !== 'select' && type !== 'date'"
      v-model="editValue"
      required
      @focus="focus"
      @blur="blur"
    />

    <!-- type = date: Field with calendar -->
    <k-calendar
      available-all
      v-if="isEditing && type === 'date'"
      :currentSetDate="editValue"
      format="Y-m-d"
      @date="(date) => { editValue = getCalendarValue(date) }"
    >
      <div class="editable-input-group input-group">
        <input
          class="editable-input editable-input-active editable-input-date"
          type="text"
          v-model="formattedDate"
          placeholder="01.01.1970"
          aria-describedby="onboarding3_calendar_addon"
          contenteditable="true"
          autocomplete="off"
          required
          v-mask="'##.##.####'"
          @focus="focus"
          @blur="blur"
        />
        <svg
          class="editable-input__calendar-img"
          width="16"
          height="18"
          viewBox="0 0 16 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.44444 0V1.8H11.5556V0H13.3333V1.8H14.2222C15.2 1.8 16 2.61 16 3.6V16.2C16 17.19 15.2 18 14.2222 18H1.77778C0.791111 18 0 17.19 0 16.2L0.00888888 3.6C0.00888888 2.61 0.791111 1.8 1.77778 1.8H2.66667V0H4.44444ZM14.2222 6.3H1.77778V16.2H14.2222V6.3Z"
            fill="#F7981E"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M8.88954 8.55078V10.3508H10.6673V12.1508H8.88865L8.88954 13.9508H7.11176L7.11087 12.1508H5.33398V10.3508H7.11176V8.55078H8.88954Z"
            fill="#F7981E"
          />
        </svg>
      </div>
    </k-calendar>

    <!-- type = select: Select field; pass `options` {Value: VisibleName}-->
    <div class="editable-input-group input-group" v-if="isEditing && type === 'select'">
      <select
        :class="['editable-input editable-input-active editable-select', {active: focused}]"
        v-model="editValue"
        required
        @focus="focus"
        @blur="blur"
      >
        <option
          :value="key"
          :selected="editValue === key"
          v-for="(option, key) in options"
          :key="key"
          :data-editvalue="editValue"
          :data-value="value"
          :data-key="key"
        >{{ option }}</option>
      </select>
      <svg
        class="editable-input__select-img"
        width="16"
        height="9"
        viewBox="0 0 16 9"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.6625 0.223756L8 6.98619L1.33754 0.223757C1.0347 -0.074585 0.529968 -0.074585 0.227129 0.223757C-0.0757102 0.522099 -0.0757101 1.01934 0.227129 1.41713L7.39432 8.77624C7.69716 9.07459 8.20189 9.07459 8.50473 8.77624L15.7729 1.51657C16.0757 1.21823 16.0757 0.720994 15.7729 0.323204C15.47 -0.0745856 14.9653 -0.0745856 14.6625 0.223756Z"
          fill="#808080"
        />
      </svg>
    </div>
    <div
      class="editable-input editable-input-inactive"
      v-if="!isEditing"
      :title="displayValue"
    >{{ displayValue }}{{ suffix || ''}}</div>
  </div>
</template>

<script>
import Calendar from "@/components/TheCalendar";
import { mask } from "vue-the-mask";
import { normalizeLength } from "../utils/helper";

export default {
  components: {
    "k-calendar": Calendar
  },
  directives: {
    mask
  },
  name: "BaseEditableInput",
  props: {
    isEditing: {
      type: Boolean,
      required: false,
      default: false
    },
    value: {
      required: false,
      default: ""
    },
    type: {
      type: String,
      required: false,
      default: "text"
    },
    name: {
      type: String,
      required: false,
      default: null
    },
    viewValue: {
      required: false,
      default: null
    },
    options: Object,
    suffix: String
  },
  data() {
    return {
      // editValue: this.value,
      focused: false,
    };
  },
  watch: {
    /*
    editValue() {
      this.$emit("edit", {
        value: this.editValue,
        name: this.name
      });
    }
    */
  },
  computed: {
    editValue: {
      get() { return this.value },
      set(value) {
        this.$emit("edit", {
          value,
          name: this.name
        });
      }
    },
    displayValue: {
      get() {
        return typeof this.viewValue === "undefined" || this.viewValue === null
          ? this.value
          : this.viewValue;
      }
    },
    formattedDate: {
      get() {
        if (!this.editValue) {
          return "";
        }
        if (typeof this.editValue === "string") {
          // year-month-day

          const parts = this.editValue.split("-");
          if (parts.length < 3) {
            return this.editValue;
          }
          const result = `${normalizeLength(parts[2], 2)}.${normalizeLength(
            parts[1],
            2
          )}.${parts[0]}`;
          return result;
        } else {
          return this.getVisibleValue(this.editValue);
        }
      },

      set(value) {
        // day.month.year
        const parts = value.split(".");

        if (parts.length < 3) {
          this.editValue = value;
        } else {
          const newParts = [parts[2], parts[1], parts[0]];
          this.editValue = newParts.join("-");
        }
      }
    }
  },
  methods: {
    getVisibleValue(date) {
      return `${normalizeLength(date.getDate(), 2)}.${normalizeLength(
        date.getMonth() + 1,
        2
      )}.${date.getFullYear()}`;
    },
    getCalendarValue(date) {
      return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    },
    focus() {
      this.focused = true;
    },
    blur() {
      this.focused = false;
    }
  }
};
</script>

<style scoped>
.editable-input-wrapper {
  border: 1px solid #cfd2d3;
  box-sizing: border-box;
  border-radius: 8px;
  color: #616161;
  display: flex;
  font-size: 14px;
  align-content: center;
  text-align: center;
  padding: 3px 10px;
}

.editable-input-wrapper.active {
  border-color: #f68c1e;
}
.editable-input-group {
  position: relative;
  flex-wrap: nowrap;
}
.editable-input-active {
  width: 100%;
}
.editable-input {
  appearance: none;
  background: none;
  border: none;
  display: inline-block;
  margin: auto;
  min-height: 1.5em;
  outline: none;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.editable-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: auto;
  margin: 0;
  outline: none;
  padding: 0 24px 0 0;
  -ms-text-align-last: center;
  text-align-last: center;
  width: 100% !important;
  position: relative;
}

.editable-input__select-img {
  pointer-events: none;
  position: absolute;
  right: 8px;
  top: 8px;
}

.editable-input-date {
  width: inherit;
}

.editable-input__calendar-img {
  flex-shrink: 0;
  margin: auto 8px;
  width: 16px;
}

.editable-input-group .editable-input {
  width: calc(100% - 24px);
}

.editable-input::-moz-focus-inner {
  outline: none;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
